'use client';

import type { Condition } from '@repo/api-gw-sdk';

import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { TextDetailsTuple } from '@/components/text/textDetailsTuple';

/** One picked value, tagged with the condition it came from. */
export interface SelectedConditionValue {
  property: string;
  value: string;
}

/**
 * The bottom selected-items drawer for a chip panel that owns several
 * conditions. `MultiSelectConditionPanel` gets the same drawer from
 * SelectionPanel for its single condition; a tree panel writes one condition
 * per level (a data class vs one of its entities, a severity vs one of its
 * controls), so the drawer spans them and remembers which property each value
 * came from in order to remove it again.
 */
export const ConditionSelectionPanel = ({
  conditions,
  onConditionsChange,
  entityName,
  labelOf,
  children,
}: React.PropsWithChildren<{
  conditions: Condition[];
  onConditionsChange: (conditions: Condition[]) => void;
  entityName: string;
  labelOf: (selected: SelectedConditionValue) => string;
}>) => {
  const items = conditions.flatMap((condition) =>
    (condition.value ?? []).map((value) => ({
      property: condition.property ?? '',
      value,
    }))
  );

  return (
    <SelectionPanel
      // The tree inside renders its own loading state.
      isLoading={false}
      drawerProps={{
        items,
        entityType: entityName,
        itemKey: (item) => `${item.property}:${item.value}`,
        renderItem: (item) => <TextDetailsTuple primaryProp={labelOf(item)} />,
        onRemove: (item) =>
          onConditionsChange(
            conditions
              .map((condition) =>
                condition.property === item.property
                  ? {
                      ...condition,
                      value: (condition.value ?? []).filter(
                        (value) => value !== item.value
                      ),
                    }
                  : condition
              )
              .filter((condition) => condition.value?.length)
          ),
        clearAll: () => onConditionsChange([]),
      }}
    >
      {children}
    </SelectionPanel>
  );
};
